import React, { useState } from 'react';
import { MultiSelect } from 'primereact/multiselect';
import { InputNumber } from '@components/framework/input/InputNumber';
import InputSelect from '@components/ringil3/Input/InputSelect/InputSelect';
import InputPriceWithCurrency from '@components/ringil3/Input/InputPriceWithCurrency/InputPriceWithCurrency';
import RingilButton from '@components/ringil3/Buttons/RingilButton';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { MoneyValue, RuleValue } from '../../ruleAst';
import { useEntityRefOptions } from './entityRefOptions';
import {
  Band,
  basisCodes,
  emptyPart,
  HANDLING_UNIT_REF_TYPE,
  KM,
  PartMode,
  PRICE,
  PRICE_TARIFF,
  PriceMode,
  priceModeHint,
  priceModeOf,
  priceRequired,
  TariffPart,
  tariffCurrency,
  tariffParts,
  withBandAdded,
  withBandFrom,
  withBandRemoved,
  withBandTo,
  withBasis,
  withField,
  withParam,
  withPartMode,
  withPriceMode,
  withTariffCurrency,
} from './priceTariff';

interface ComponentProps {
  action: string;
  params: Record<string, RuleValue>;
  onChange: (params: Record<string, RuleValue>) => void;
  companyId?: string;
  readOnly?: boolean;
}

/**
 * The price an action asks for or assigns at: none, a fixed `price`, or a `price_tariff` computed
 * from the expedition with the fixed price as its fallback. One choice instead of two independent
 * fields, so "tariff" reads as the primary price and `price` as what applies when it does not.
 */
const PriceSection: React.FC<ComponentProps> = ({ action, params, onChange, companyId, readOnly }) => {
  const { tr } = useTranslationLgs();
  const required = priceRequired(action);
  // Remembers the choice while nothing is entered yet, when the parameters cannot say it.
  const [chosenMode, setChosenMode] = useState<PriceMode>(required ? 'FIXED' : 'NONE');
  const mode = priceModeOf(params, chosenMode, required);
  // The currency picked (or defaulted by the picker) before any price holds it, for the next one entered.
  const [chosenCurrency, setChosenCurrency] = useState<string>(undefined);
  const currency = tariffCurrency(params) ?? chosenCurrency;
  const hint = priceModeHint(mode);

  const modeOptions: { code: PriceMode; label: string }[] = [
    ...(required ? [] : [{ code: 'NONE' as PriceMode, label: tr('AiDispatcher.priceMode.none', 'None') }]),
    { code: 'FIXED', label: tr('AiDispatcher.priceMode.fixed', 'Fixed') },
    { code: 'TARIFF', label: tr('AiDispatcher.priceMode.tariff', 'Tariff') },
  ];

  const parts = tariffParts(params[PRICE_TARIFF]);
  const changeParts = (next: TariffPart[]) => onChange({ ...params, [PRICE_TARIFF]: next });
  const changePart = (index: number, part: TariffPart) => changeParts(parts.map((current, i) => (i === index ? part : current)));

  // A currency picked anywhere is applied to every price in the tariff — they must share one.
  const priceInput = (price: MoneyValue | undefined, set: (price: MoneyValue | undefined) => void, prefillCurrency = currency) => (
    <InputPriceWithCurrency
      value={price?.value ?? null}
      currency={price?.currency ?? prefillCurrency ?? null}
      changePrice={amount => set(amount === null || amount === undefined ? undefined : { value: amount, currency: price?.currency ?? prefillCurrency ?? null })}
      changeCurrency={code => {
        setChosenCurrency(code);
        // The picker reports its currency on every mount; an unchanged rule must not be marked edited.
        const next = withTariffCurrency(params, code);
        if (JSON.stringify(next) !== JSON.stringify(params)) onChange(next);
      }}
      companyId={companyId}
      disabled={readOnly}
    />
  );

  const fixedPrice = params[PRICE] as MoneyValue | undefined;
  const setFixedPrice = (price: MoneyValue | undefined) => onChange(withParam(params, PRICE, price));

  return (
    <div className="flex flex-col gap-3 md:col-span-2">
      <div className="flex flex-col gap-1">
        <span className="text-sm text-content-secondary">
          {tr('AiDispatcher.priceMode.label', 'Price')}
          {required && <span className="ml-1 text-content-brand">*</span>}
        </span>
        <InputSelect
          model={modeOptions}
          selectedItemCode={mode}
          onSelect={code => {
            setChosenMode(code as PriceMode);
            onChange(withPriceMode(params, code as PriceMode));
          }}
          disabled={readOnly}
          showCheckIcon={false}
        />
        <span className="text-xs text-content-tertiary">{tr(hint.key, hint.text)}</span>
      </div>

      {mode === 'FIXED' && <div className="md:w-1/2">{priceInput(fixedPrice, setFixedPrice)}</div>}

      {mode === 'TARIFF' && (
        <>
          <div className="flex flex-col gap-2">
            <span className="text-sm text-content-secondary">{tr('AiDispatcher.tariff.parts', 'Tariff parts')}</span>
            {parts.map((part, index) => (
              <TariffPartInput
                key={index}
                part={part}
                onChange={next => changePart(index, next)}
                onRemove={parts.length > 1 ? () => changeParts(parts.filter((_, i) => i !== index)) : undefined}
                priceInput={priceInput}
                companyId={companyId}
                readOnly={readOnly}
              />
            ))}
            {!readOnly && (
              <div>
                <RingilButton mode="text-only" label={tr('AiDispatcher.tariff.addPart', '+ Add a part')} onClick={() => changeParts([...parts, emptyPart()])} />
              </div>
            )}
          </div>
          <div className="flex flex-col gap-1 md:w-1/2">
            <span className="text-sm text-content-secondary">{tr('AiDispatcher.tariff.fallback', 'Fallback when the tariff does not apply (optional)')}</span>
            {priceInput(fixedPrice, setFixedPrice)}
          </div>
        </>
      )}
    </div>
  );
};

interface PartProps {
  part: TariffPart;
  onChange: (part: TariffPart) => void;
  onRemove?: () => void;
  priceInput: (price: MoneyValue | undefined, set: (price: MoneyValue | undefined) => void) => React.ReactNode;
  companyId?: string;
  readOnly?: boolean;
}

/** One part of a tariff: what it measures, and a rate per unit or a price per range. */
const TariffPartInput: React.FC<PartProps> = ({ part, onChange, onRemove, priceInput, companyId, readOnly }) => {
  const { tr } = useTranslationLgs();
  const handlingUnits = useEntityRefOptions(HANDLING_UNIT_REF_TYPE, companyId);
  const selected = basisCodes(part.by);
  // `km` first, then the company's handling units. A stored code missing from the list (a template
  // since deleted) is still shown, so it can be seen and removed.
  const known = new Set(handlingUnits.options.map(option => option.value));
  const basisOptions = [
    { label: tr('AiDispatcher.tariff.km', 'km (distance)'), value: KM },
    ...handlingUnits.options.map(option => ({ label: `${option.label} (${option.value})`, value: option.value })),
    ...selected.filter(code => code !== KM && !known.has(code)).map(code => ({ label: code, value: code })),
  ];
  const unitLabel = selected.includes(KM) ? 'km' : tr('AiDispatcher.tariff.unit', 'unit');
  const bands = part.bands ?? [];
  const changeBands = (next: Band[]) => onChange(withField(part, 'bands', next));

  return (
    <div className="flex flex-col gap-3 rounded-md border border-solid border-base-tertiary p-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex min-w-[14rem] flex-1 flex-col gap-1">
          <span className="text-sm text-content-secondary">{tr('AiDispatcher.tariff.by', 'Measure')}</span>
          <MultiSelect
            className="w-full"
            value={selected}
            options={basisOptions}
            onChange={event => onChange(withBasis(part, event.value ?? []))}
            disabled={readOnly || handlingUnits.loading}
            filter={basisOptions.length > 10}
            display="chip"
            placeholder={handlingUnits.loading ? tr('AiDispatcher.loadingOptions', 'Loading…') : tr('AiDispatcher.selectValues', 'Select values')}
          />
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-sm text-content-secondary">{tr('AiDispatcher.tariff.mode', 'Mode')}</span>
          <InputSelect
            model={[
              { code: 'unit_price', label: tr('AiDispatcher.tariff.unitPrice', 'Per unit') },
              { code: 'range_price', label: tr('AiDispatcher.tariff.rangePrice', 'Ranges') },
            ]}
            selectedItemCode={part.kind}
            onSelect={code => onChange(withPartMode(part, code as PartMode))}
            disabled={readOnly}
            showCheckIcon={false}
          />
        </div>
        {onRemove && !readOnly && <RingilButton mode="text-only" label={tr('AiDispatcher.remove', 'Remove')} onClick={onRemove} />}
      </div>

      {part.kind === 'unit_price' ? (
        <div className="flex flex-col gap-1 md:w-1/2">
          <span className="text-sm text-content-secondary">{tr('AiDispatcher.tariff.pricePer', 'Price per {{unit}}', { unit: unitLabel })}</span>
          {priceInput(part.price, price => onChange(withField(part, 'price', price)))}
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {bands.map((band, index) => (
            <div key={index} className="flex flex-wrap items-end gap-2">
              <BoundInput
                label={tr('AiDispatcher.tariff.from', 'From')}
                value={band.from}
                onChange={from => changeBands(withBandFrom(bands, index, from))}
                readOnly={readOnly}
              />
              <BoundInput
                label={tr('AiDispatcher.tariff.to', 'To')}
                value={band.to}
                onChange={to => changeBands(withBandTo(bands, index, to))}
                readOnly={readOnly}
                placeholder={index === bands.length - 1 ? tr('AiDispatcher.tariff.openEnded', 'and more') : undefined}
              />
              <div className="flex min-w-[14rem] flex-1 flex-col gap-1">
                <span className="text-sm text-content-secondary">{tr('AiDispatcher.tariff.bandPrice', 'Price')}</span>
                {priceInput(band.price, price => changeBands(bands.map((current, i) => (i === index ? withField(current, 'price', price) : current))))}
              </div>
              {bands.length > 1 && !readOnly && (
                <RingilButton mode="text-only" label={tr('AiDispatcher.remove', 'Remove')} onClick={() => changeBands(withBandRemoved(bands, index))} />
              )}
            </div>
          ))}
          <span className="text-xs text-content-tertiary">
            {tr('AiDispatcher.tariff.bandsHint', '"From" is included, "To" is not. Leave "To" empty on the last range for "and more".')}
          </span>
          {!readOnly && (
            <div>
              <RingilButton mode="text-only" label={tr('AiDispatcher.tariff.addBand', '+ Add a range')} onClick={() => changeBands(withBandAdded(bands))} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const BoundInput: React.FC<{ label: string; value: number | undefined; onChange: (value: number | null) => void; readOnly?: boolean; placeholder?: string }> = ({
  label,
  value,
  onChange,
  readOnly,
  placeholder,
}) => (
  <div className="flex flex-col gap-1">
    <span className="text-sm text-content-secondary">{label}</span>
    <InputNumber
      className="w-28"
      inputClassName="h-11 w-full"
      value={value ?? null}
      onValueChange={event => onChange(event.value ?? null)}
      disabled={readOnly}
      mode="decimal"
      min={0}
      minFractionDigits={0}
      maxFractionDigits={2}
      placeholder={placeholder}
    />
  </div>
);

export default PriceSection;
